import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, AuthUser, CollegeProfile, CoordinatorProfile } from '@/services/auth.service';
import { ROUTES } from '@/routes/routes';
import { deleteDeviceToken } from '@/api/queries/auth.queries';

interface AuthContextType {
  user: AuthUser | null;
  collegeProfile: CollegeProfile | null;
  coordinatorProfile: CoordinatorProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  setCollegeProfile: (profile: CollegeProfile | null) => void;
  setCoordinatorProfile: (profile: CoordinatorProfile | null) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [collegeProfile, setCollegeProfile] = useState<CollegeProfile | null>(null);
  const [coordinatorProfile, setCoordinatorProfile] = useState<CoordinatorProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // SSO hand-off: if the landing URL carries a `?token=<jwt>` we treat it as
        // a freshly issued access token, persist it, and immediately strip the
        // param from the URL so the JWT doesn't sit in browser history / referers.
        try {
          const params = new URLSearchParams(window.location.search);
          const ssoToken = params.get('token');
          if (ssoToken) {
            // No refresh token is sent over SSO; store an empty string so the
            // axios interceptor short-circuits the refresh flow and lets the
            // session expire naturally when the access token does.
            await authService.saveLoginData(ssoToken, '');

            // Remove the token (and only the token) from the URL without
            // triggering a navigation/remount.
            params.delete('token');
            const cleanedSearch = params.toString();
            const cleanedUrl =
              window.location.pathname +
              (cleanedSearch ? `?${cleanedSearch}` : '') +
              window.location.hash;
            window.history.replaceState({}, '', cleanedUrl);
          }
        } catch (ssoError) {
          console.error('SSO token hand-off failed:', ssoError);
        }

        const isValid = await authService.validateToken();
        if (isValid) {
          const userData = await authService.getCurrentUser();
          setUser(userData);
        } else {
          // Clear any stale data
          await authService.clearAuthData();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        await authService.clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (accessToken: string, refreshToken: string) => {
    try {
      await authService.saveLoginData(accessToken, refreshToken);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      // Navigation is handled by the calling component (LoginPage)
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Try to delete device token but don't block logout if it fails
    try {
      await deleteDeviceToken();
    } catch (error) {
      console.warn('Failed to delete device token:', error);
    }

    // Always proceed with logout regardless of deleteDeviceToken result
    try {
      await authService.clearAuthData();
    } catch (error) {
      console.error('Failed to clear auth data:', error);
    }

    setUser(null);
    setCollegeProfile(null);
    setCoordinatorProfile(null);
    navigate(ROUTES.signIn, { replace: true });
  };

  const value: AuthContextType = {
    user,
    collegeProfile,
    coordinatorProfile,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    setCollegeProfile,
    setCoordinatorProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
